import {announce} from '@github-ui/aria-live'
import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import {Link} from '@github-ui/react-router'
import {FileIcon, SearchIcon} from '@primer/octicons-react'
import {FormControl, Label, NavList, SplitPageLayout, TextInput} from '@primer/react'
import {useEffect, useEffectEvent, useRef, useState} from 'react'
import {shortOid} from '../toolbar/timeline-panel/issue-events/short-oid'
import {ArtifactMetadata} from './ArtifactMetadata'
import {ArtifactsBlankSlate} from './ArtifactsBlankSlate'
import {useSelectedPullRequestArtifact} from './use-selected-pull-request-artifact'
import styles from './ArtifactsContent.module.css'

export const ArtifactsContent = () => {
  // TODO: Announce loading, error, empty, and artifact selection changes to screen readers.
  const {artifacts, isPending, isLoadingError, selectedArtifact, isNotFound, getArtifactLinkProps} =
    useSelectedPullRequestArtifact()
  const [filter, setFilter] = useState('')
  const normalizedFilter = filter.trim().toLowerCase()
  const filteredArtifacts = artifacts.filter(artifact => artifact.name.toLowerCase().includes(normalizedFilter))
  const resultCount = filteredArtifacts.length
  const previousFilter = useRef(normalizedFilter)

  const announceResults = useEffectEvent(() => {
    if (artifacts.length === 0) return

    announce(
      resultCount === 0
        ? 'No artifacts match your search'
        : `${resultCount} ${resultCount === 1 ? 'artifact' : 'artifacts'} found`,
    )
  })

  useEffect(() => {
    if (previousFilter.current === normalizedFilter) return
    previousFilter.current = normalizedFilter

    const timeout = setTimeout(() => {
      announceResults()
    }, 300)
    return () => clearTimeout(timeout)
  }, [normalizedFilter])

  if (isPending) return <ArtifactsBlankSlate heading="Loading artifacts…" />
  if (isLoadingError) return <ArtifactsBlankSlate heading="Unable to load artifacts" />

  if (artifacts.length === 0 && !isNotFound) {
    return (
      <ArtifactsBlankSlate
        heading="No artifacts yet"
        description="Artifacts created for this pull request will appear here"
      />
    )
  }

  return (
    <SplitPageLayout>
      <SplitPageLayout.Pane position="start" sticky={false} padding="condensed">
        <FormControl className={styles.Filter}>
          <FormControl.Label visuallyHidden>Filter artifacts</FormControl.Label>
          <TextInput
            type="search"
            leadingVisual={SearchIcon}
            block
            value={filter}
            onChange={event => setFilter(event.target.value)}
            placeholder="Filter artifacts"
          />
        </FormControl>
        <NavList aria-label="Artifacts">
          {filteredArtifacts.map(artifact => (
            <NavList.Item
              key={artifact.id}
              as={Link}
              {...getArtifactLinkProps(artifact.number)}
              aria-current={selectedArtifact?.id === artifact.id ? 'page' : undefined}
            >
              <span className={styles.ArtifactName}>{artifact.name}</span>
            </NavList.Item>
          ))}
        </NavList>
        {normalizedFilter !== '' && resultCount === 0 && (
          <p className={styles.NoResults}>No artifacts match your search</p>
        )}
      </SplitPageLayout.Pane>
      <SplitPageLayout.Content as="div" width="full">
        {selectedArtifact ? (
          <section aria-labelledby="selected-artifact-heading" className={styles.Artifact}>
            <div className={styles.Header}>
              <h2 id="selected-artifact-heading" className={styles.Heading}>
                {selectedArtifact.name}
              </h2>
              {selectedArtifact.commit_oid && (
                <Label className={styles.Sha}>{shortOid(selectedArtifact.commit_oid)}</Label>
              )}
            </div>
            <ArtifactMetadata artifact={selectedArtifact} />
            <div className={styles.File}>
              <div className={styles.FileHeader}>
                <FileIcon className={styles.FileIcon} />
                <span className={styles.FileName}>{selectedArtifact.name}</span>
                <span>Markdown preview</span>
              </div>
              <div className={styles.FileBody}>
                <NewMarkdownViewer key={selectedArtifact.id} verifiedHTML={selectedArtifact.body} />
              </div>
            </div>
          </section>
        ) : (
          <ArtifactsBlankSlate
            heading="404 - Artifact not found"
            description="The requested artifact could not be found."
          />
        )}
      </SplitPageLayout.Content>
    </SplitPageLayout>
  )
}
